import { ToastrService } from 'ngx-toastr';
import {
  FilterBuilderParam,
  FilterBuilderOutput
} from 'src/app/models/filter-builder';
import { Component, OnInit } from '@angular/core';
import {
  WorkflowContextService,
  DataService,
  ContractorService
} from 'src/app/services';
import { PagedContractorSummaryReportResponse } from 'src/app/models/contractor-reports';
import { Utilities } from 'src/app/services';
import { ColumnOption, GridSettings } from 'src/app/models/grid-settings';
import { TableColumn } from '@swimlane/ngx-datatable';
import { ComponentIds } from 'src/app/models/component-ids';

@Component({
    selector: 'wm-contractor-summary',
    templateUrl: './summary.component.html',
    styleUrls: ['./summary.component.css'],
    standalone: false
})
export class ContractorSummaryComponent implements OnInit {
  id = ComponentIds.CONTRACTOR_SUMMARY;
  loading = true;
  report: PagedContractorSummaryReportResponse;
  params: FilterBuilderParam[] = [
    {
      name: 'Created Date',
      id: 'timeframe',
      inputType: 'date',
      types: ['range']
    }
  ];
  filters: FilterBuilderOutput = {};

  public useExcelFormat: boolean;

  columns: TableColumn[];
  availableColumns: TableColumn[];
  columnOptions: ColumnOption[] = [];
  exportColumns: string[];
  rows: any[];
  public page = {
    // The number of elements in the page
    size: 10,
    // The total number of elements
    totalElements: 0,
    // The total number of pages
    totalPages: 0,
    // The current page number
    pageNumber: 0
  };
  public sortField: string;
  public sortDescending = false;

  // column prop strings to be hidden on the grid by default
  defaultHiddenColumns: string[] = [];
  // column prop strings that should have no option to hide in the grid, and are not exportable (such as button columns like Refund or Actions)
  actionColumns: string[] = [];
  // column prop strings to be excluded from exports by default
  defaultExportExludedColumns: string[] = [];

  constructor(
    private contractorSvc: ContractorService,
    private dataSvc: DataService,
    private toast: ToastrService,
    private _context: WorkflowContextService
  ) {}

  ngOnInit() {
    this.columns = [
      {
        prop: 'registrationType',
        name: 'Registration Type'
      },
      {
        prop: 'pending',
        name: 'Pending'
      },
      {
        prop: 'approved',
        name: 'Approved'
      },
      {
        prop: 'denied',
        name: 'Denied'
      },
      {
        prop: 'expired',
        name: 'Expired'
      },
      {
        prop: 'inactive',
        name: 'Inactive'
      },
      {
        prop: 'fees',
        name: 'Fees'
      }
    ];
    this.setAvailableColumns();
  }

  setAvailableColumns() {
    this.availableColumns = this.columns;
  }

  handleSettingsChanged(event: GridSettings) {
    this.filters = event.filters;
    this.page.size = event.pageSize;
    this.page.pageNumber = event.pageNumber;
    this.sortField = event.sortField;
    this.sortDescending = event.sortDescending;
    this.columnOptions = event.columnOptions;

    const visibleColumnProps = this.columnOptions
      .filter(co => co.checked)
      .map(co => co.name);

    this.columns = this.availableColumns.filter(
      c =>
        visibleColumnProps.includes(c.prop as string) ||
        this.actionColumns.includes(c.prop as string)
    );

    this.exportColumns = this.columnOptions
      .filter(co => co.includeInExport)
      .map(co => co.name);

    this.getReport();
  }

  handleColumnOptionsChanged(event: ColumnOption[]) {
    this.columnOptions = event;

    this.exportColumns = this.columnOptions
      .filter(co => co.includeInExport)
      .map(co => co.name);
  }

  getQuery() {
    const timeframe = this.filters['timeframe'];

    const query = {
      clientId: this._context.client.id,
      timeframe: timeframe && timeframe.value,
      useExcelFormat: this.useExcelFormat,
      sortField: this.sortField,
      sortDescending: this.sortDescending,
      exportColumns: this.exportColumns
    };

    return query;
  }

  getPagedQuery() {
    const timeframe = this.filters['timeframe'];

    const query = {
      clientId: this._context.client.id,
      timeframe: timeframe && timeframe.value,
      pageNumber: this.page.pageNumber,
      pageSize: this.page.size,
      sortField: this.sortField,
      sortDescending: this.sortDescending
    };

    return query;
  }

  getReport() {
    this.loading = true;

    const query = this.getPagedQuery();

    this.contractorSvc
      .getPagedContractorSummaryReport(query)
      .subscribe(report => {
        this.page.totalElements = report.recordCount;
        this.page.totalPages = report.pageCount;
        this.rows = report.items.map(i => {
          i['registrationType'] = i.registrationType;
          i['pending'] = i.pending;
          i['approved'] = i.approved;
          i['denied'] = i.denied;
          i['expired'] = i.expired;
          i['inactive'] = i.inactive;
          i['fees'] = Utilities.formatCurrency(i.fees);
          return i;
        });
        this.report = report;
        this.loading = false;
      });
  }

  exportExcel() {
    this.useExcelFormat = true;
    this.export();
  }

  exportCsv() {
    this.useExcelFormat = false;
    this.export();
  }

  export() {
    const query = this.getQuery();
    this.dataSvc.exportContractorSummaryReport(query).subscribe(
      data => {
        if (this.useExcelFormat) {
          const blob = new Blob([data], {
            type:
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          });
          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.download = `contractor-summary-report-export-${new Date().getTime()}.xlsx`;
          link.click();
        } else {
          const blob = new Blob([data], { type: 'text/csv' });
          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.download = `contractor-summary-report-export-${new Date().getTime()}.csv`;
          link.click();
        }
      },
      err => {
        this.toast.error('Problem while downloading the file.');
        console.error(err);
      }
    );
  }
}
